const MCP_SSE_URL =
  process.env.REACT_APP_MCP_URL ||
  (process.env.NODE_ENV === "development" ? "/mcp-api/sse" : "http://17.224.50.181:8138/sse");
const MCP_TOKEN = process.env.REACT_APP_MCP_TOKEN || "123456789";
const MCP_PROTOCOL_VERSION = "2024-11-05";

let requestId = 0;
let initialized = false;
let toolsCache = null;
let sessionPromise = null;
let messageUrl = null;
let sseAbort = null;
const pending = new Map();

function buildHeaders(extra = {}) {
  return {
    Authorization: `Bearer ${MCP_TOKEN}`,
    "MCP-Protocol-Version": MCP_PROTOCOL_VERSION,
    ...extra,
  };
}

/** Map server endpoint path to a browser-reachable URL (via dev proxy when needed). */
function resolveMessageUrl(endpointPath) {
  if (MCP_SSE_URL.startsWith("/")) {
    const path = endpointPath.startsWith("/") ? endpointPath : `/${endpointPath}`;
    return `/mcp-api${path}`;
  }
  return new URL(endpointPath, MCP_SSE_URL).toString();
}

function parseSseBlock(block) {
  const lines = block.split("\n");
  let event = "message";
  const dataLines = [];

  for (const line of lines) {
    if (line.startsWith("event:")) event = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
  }

  return { event, data: dataLines.join("\n") };
}

function handleSseData(event, data) {
  if (!data) return;

  if (event === "endpoint") {
    messageUrl = resolveMessageUrl(data);
    return;
  }

  try {
    const json = JSON.parse(data);
    if (json?.id != null && pending.has(json.id)) {
      const { resolve, reject } = pending.get(json.id);
      pending.delete(json.id);
      if (json.error) {
        reject(new Error(json.error.message || "MCP request failed."));
      } else {
        resolve(json.result ?? json);
      }
    }
  } catch (_) {
    /* ignore non-JSON SSE frames */
  }
}

async function readSseStream(response, onEndpoint) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
    const parts = buffer.split("\n\n");
    buffer = parts.pop() || "";

    for (const part of parts) {
      if (!part.trim()) continue;
      const { event, data } = parseSseBlock(part);
      if (event === "endpoint" && data) {
        messageUrl = resolveMessageUrl(data);
        onEndpoint?.(messageUrl);
      } else {
        handleSseData(event, data);
      }
    }
  }
}

function rejectAllPending(error) {
  for (const { reject } of pending.values()) {
    reject(error);
  }
  pending.clear();
}

async function ensureSession() {
  // Reuse only a live session (messageUrl cleared when SSE drops).
  if (sessionPromise && messageUrl) return sessionPromise;
  if (sessionPromise && !messageUrl) {
    // Still connecting — wait for the in-flight handshake.
    return sessionPromise;
  }

  sseAbort = new AbortController();

  sessionPromise = (async () => {
    let response;
    try {
      response = await fetch(MCP_SSE_URL, {
        method: "GET",
        headers: buildHeaders({ Accept: "text/event-stream" }),
        signal: sseAbort.signal,
      });
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      throw new Error("Unable to reach the MCP server. Check the URL and CORS settings.");
    }

    if (!response.ok) {
      const body = await response.text().catch(() => "");
      throw new Error(
        body?.trim()?.slice(0, 200) || `MCP SSE connection failed (${response.status}).`
      );
    }

    const endpointReady = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error("Timed out waiting for MCP session.")), 15000);
      readSseStream(response, (url) => {
        clearTimeout(timeout);
        resolve(url);
      }).catch((error) => {
        clearTimeout(timeout);
        if (error?.name !== "AbortError") {
          rejectAllPending(error);
        }
        messageUrl = null;
        sessionPromise = null;
        initialized = false;
      });
    });

    await endpointReady;
    return messageUrl;
  })().catch((error) => {
    sessionPromise = null;
    messageUrl = null;
    throw error;
  });

  return sessionPromise;
}

async function mcpRequest(method, params = {}, { signal, isNotification = false } = {}) {
  await ensureSession();

  if (!messageUrl) {
    throw new Error("MCP session is not ready. Please try again.");
  }

  const payload = { jsonrpc: "2.0", method, params };
  let waitForResponse = null;

  if (!isNotification) {
    requestId += 1;
    payload.id = requestId;
    waitForResponse = new Promise((resolve, reject) => {
      pending.set(payload.id, { resolve, reject });
      if (signal) {
        const onAbort = () => {
          pending.delete(payload.id);
          reject(Object.assign(new Error("Aborted"), { name: "AbortError" }));
        };
        if (signal.aborted) onAbort();
        else signal.addEventListener("abort", onAbort, { once: true });
      }
      setTimeout(() => {
        if (pending.has(payload.id)) {
          pending.delete(payload.id);
          reject(new Error(`MCP request timed out (${method}).`));
        }
      }, 60000);
    });
  }

  let response;
  try {
    response = await fetch(messageUrl, {
      method: "POST",
      headers: buildHeaders({
        "Content-Type": "application/json",
        Accept: "application/json, text/event-stream",
      }),
      body: JSON.stringify(payload),
      signal,
    });
  } catch (error) {
    if (payload.id != null) pending.delete(payload.id);
    if (error?.name === "TypeError") {
      throw new Error("Unable to reach the MCP server. Check the URL and CORS settings.");
    }
    throw error;
  }

  if (!response.ok && response.status !== 202) {
    if (payload.id != null) pending.delete(payload.id);
    let message = `MCP request failed (${response.status}).`;
    try {
      const errText = await response.text();
      if (errText) message = errText.slice(0, 200);
    } catch (_) {
      /* keep default */
    }
    throw new Error(message);
  }

  if (isNotification) return null;
  return waitForResponse;
}

export async function initializeMcp(signal) {
  if (initialized) return;

  await mcpRequest(
    "initialize",
    {
      protocolVersion: MCP_PROTOCOL_VERSION,
      capabilities: {},
      clientInfo: { name: "partner-portal", version: "1.0.0" },
    },
    { signal }
  );

  await mcpRequest("notifications/initialized", {}, { signal, isNotification: true });
  initialized = true;
}

export async function listMcpTools(signal, { refresh = false } = {}) {
  await initializeMcp(signal);

  if (!refresh && toolsCache) {
    return toolsCache;
  }

  const result = await mcpRequest("tools/list", {}, { signal });
  toolsCache = Array.isArray(result?.tools) ? result.tools : [];
  return toolsCache;
}

export async function callMcpTool(name, args = {}, signal) {
  await initializeMcp(signal);
  return mcpRequest("tools/call", { name, arguments: args }, { signal });
}

export function resetMcpSession() {
  initialized = false;
  toolsCache = null;
  messageUrl = null;
  sessionPromise = null;
  rejectAllPending(new Error("MCP session reset."));
  if (sseAbort) {
    sseAbort.abort();
    sseAbort = null;
  }
}
